import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import { IAuthConfig } from '@models';
import { AuthService } from '@services';
import { unsubscribe } from '@utils/object.utils';
import UiValidators, {
    UI_EMAIL_MAX_LENGTH,
    UI_NAME_LONG_MAX_LENGTH,
    UI_NAME_MAX_LENGTH,
    UI_PASSWORD_MAX_LENGTH,
} from '@validators';

import {
    BehaviorSubject,
    combineLatest,
    Observable,
    Subscription,
} from 'rxjs';
import {
    finalize,
    switchMap,
    tap,
} from 'rxjs/operators';

@Component({
    templateUrl: './register-tenant.page.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterTenantPageComponent implements OnInit, OnDestroy {
    public loading$ = new BehaviorSubject(false);
    public form!: FormGroup;
    public registrationType!: string;
    public authConfig$: Observable<IAuthConfig>;

    private _routeSub!: Subscription;

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _authService: AuthService,
        private _fb: FormBuilder,
        private _appState: AppStateFacade,
    ) {
        this.authConfig$ = this._appState.authConfig.value$;
    }

    ngOnInit() {
        this._routeSub =
            combineLatest([
                this._route.queryParams
                    .pipe(
                        tap((result) => {
                            this.registrationType = result['type'];
                            this._initFormGroup();
                        }),
                    ),
                this._authService.getExternalLoginInfo(),
            ])
                .subscribe(([_, data]) => {
                    if (this.registrationType === 'external') {
                        if (data) {
                            this.form.get('TenancyName')!.patchValue(data.tenancyName);
                            this.form.get('EmailAddress')!.patchValue(data.emailAddress);
                            this.form.get('Name')!.patchValue(data.name);
                            this.form.get('Surname')!.patchValue(data.surname);
                            this.form.get('RegistrationType')!.patchValue(this.registrationType);
                        }
                    } else {
                        this.form.get('Password')!.setValidators(
                            Validators.compose([
                                Validators.required,
                                Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
                                UiValidators.complexity(this._appState.authConfig.value.passwordComplexity),
                            ]),
                        );

                        this.form.get('ConfirmPassword')!.setValidators(
                            Validators.compose([
                                Validators.required,
                                Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
                            ]),
                        );
                    }
                });
    }

    ngOnDestroy() {
        unsubscribe(
            this._routeSub,
        );
    }

    public onSubmit(event: Event) {
        event.preventDefault();

        if (!this.form.valid) {
            return;
        }

        this.loading$.next(true);

        this._authService.registerTenant(this.form.value)
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
                tap(() => this._authService.lastTenantUsed = this.form.get('TenancyName')!.value),
                switchMap(() => this._appState.user.fetch()),
                switchMap(() => this._appState.user.ensureCorrectIdentity()),
            )
            .subscribe(() => this._router.navigate(['/']));
    }

    private _initFormGroup() {
        this.form = this._fb.group({
            'TenancyName': ['', Validators.compose([
                Validators.maxLength(UI_NAME_LONG_MAX_LENGTH),
                UiValidators.regex('^[\\p{L}][\\p{L}0-9-_]+$'),
            ])],
            'UserName': ['admin', UiValidators.required()],
            'Name': ['', Validators.compose([
                Validators.required,
                Validators.maxLength(UI_NAME_MAX_LENGTH),
            ])],
            'Surname': ['', Validators.compose([
                Validators.required,
                Validators.maxLength(UI_NAME_MAX_LENGTH),
            ])],
            'EmailAddress': ['', Validators.compose([
                UiValidators.email(true),
                Validators.maxLength(UI_EMAIL_MAX_LENGTH),
            ])],
            'RegistrationType': ['internal'],
            'Password': [''],
            'ConfirmPassword': [''],
        }, {
                validator: UiValidators.confirmPassword('Password', 'ConfirmPassword'),
            });

        if (this._appState.authConfig.value.application.termsAndConditionsRegisterUrl) {
            const termsControl = new FormControl(false, UiValidators.regex(/^true$/));
            this.form.addControl('TermsAndConditionsAccepted', termsControl);
        }

        if (!this._appState.authConfig.value.application.isTenantRegistrationEnabled) {
            // go to dashboard if registration is disabled
            this._router.navigate(['/']);
        }
    }
}
